import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import leadService from '../services/leadService';

const initialState = {
  leads: [],
  currentLead: null,
  pagination: null,
  loading: false,
  error: null,
  importLogs: [],
  filters: {
    status: [],
    source: '',
    state: '',
    city: '',
    assigned: null,
    search: '',
  },
};

// Get Leads
export const getLeads = createAsyncThunk(
  'lead/getLeads',
  async (params, { rejectWithValue }) => {
    try {
      const data = await leadService.getLeads(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get Leads by Project
export const getLeadsByProject = createAsyncThunk(
  'lead/getLeadsByProject',
  async ({ projectId, params }, { rejectWithValue }) => {
    try {
      const data = await leadService.getLeadsByProject(projectId, params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get Lead
export const getLead = createAsyncThunk(
  'lead/getLead',
  async (id, { rejectWithValue }) => {
    try {
      const data = await leadService.getLead(id);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create Lead
export const createLead = createAsyncThunk(
  'lead/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      const data = await leadService.createLead(leadData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Lead
export const updateLead = createAsyncThunk(
  'lead/updateLead',
  async ({ id, leadData }, { rejectWithValue }) => {
    try {
      const data = await leadService.updateLead(id, leadData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Lead
export const deleteLead = createAsyncThunk(
  'lead/deleteLead',
  async (id, { rejectWithValue }) => {
    try {
      await leadService.deleteLead(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Change Status
export const changeStatus = createAsyncThunk(
  'lead/changeStatus',
  async ({ id, statusData }, { rejectWithValue }) => {
    try {
      const data = await leadService.changeStatus(id, statusData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Mark Contacted
export const markContacted = createAsyncThunk(
  'lead/markContacted',
  async ({ id, notes }, { rejectWithValue }) => {
    try {
      const data = await leadService.markContacted(id, { notes });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Bulk Assign
export const bulkAssign = createAsyncThunk(
  'lead/bulkAssign',
  async (assignData, { rejectWithValue }) => {
    try {
      const data = await leadService.bulkAssign(assignData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Import Leads
export const importLeads = createAsyncThunk(
  'lead/importLeads',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await leadService.importLeads(formData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get Import Logs
export const getImportLogs = createAsyncThunk(
  'lead/getImportLogs',
  async (params, { rejectWithValue }) => {
    try {
      const data = await leadService.getImportLogs(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add Note
export const addNote = createAsyncThunk(
  'lead/addNote',
  async ({ id, content }, { rejectWithValue }) => {
    try {
      const data = await leadService.addNote(id, { content });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Upload Document
export const uploadDocument = createAsyncThunk(
  'lead/uploadDocument',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = await leadService.uploadDocument(id, formData);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const leadSlice = createSlice({
  name: 'lead',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLead: (state) => {
      state.currentLead = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Leads
      .addCase(getLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Leads by Project
      .addCase(getLeadsByProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLeadsByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getLeadsByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Lead
      .addCase(getLead.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLead.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
      })
      .addCase(getLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Lead
      .addCase(createLead.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.unshift(action.payload);
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Lead
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
        const index = state.leads.findIndex(l => l.id === action.payload.id);
        if (index !== -1) {
          state.leads[index] = action.payload;
        }
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Lead
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.leads = state.leads.filter(l => l.id !== action.payload);
      })
      // Change Status
      .addCase(changeStatus.fulfilled, (state, action) => {
        state.currentLead = action.payload;
        const index = state.leads.findIndex(l => l.id === action.payload.id);
        if (index !== -1) {
          state.leads[index] = { ...state.leads[index], status: action.payload.status };
        }
      })
      // Mark Contacted
      .addCase(markContacted.fulfilled, (state, action) => {
        state.currentLead = action.payload;
      })
      // Import Leads
      .addCase(importLeads.pending, (state) => {
        state.loading = true;
      })
      .addCase(importLeads.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Import Logs
      .addCase(getImportLogs.fulfilled, (state, action) => {
        state.importLogs = action.payload.data;
      })
      // Add Note
      .addCase(addNote.fulfilled, (state, action) => {
        if (state.currentLead) {
          state.currentLead.lead_notes.unshift(action.payload);
        }
      })
      // Upload Document
      .addCase(uploadDocument.fulfilled, (state, action) => {
        if (state.currentLead) {
          state.currentLead.documents.unshift(action.payload);
        }
      });
  },
});

export const { clearError, clearCurrentLead, setFilters, clearFilters } = leadSlice.actions;
export default leadSlice.reducer;